import type { Connection, Organization, Todo, TodoDraft } from "./types";
import { DEFAULT_API_URL } from "./config";

type TodoListResponse = {
  todos: Todo[];
  next_cursor?: string;
  list_complete: boolean;
};

type OrganizationListResponse = {
  organizations: Organization[];
};

function compactDraft(draft: TodoDraft) {
  return Object.fromEntries(
    Object.entries(draft).filter(([, value]) => value !== ""),
  );
}

async function request<T>(
  connection: Connection,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = connection.token.trim().replace(/^Bearer\s+/i, "");
  const configuredUrl = connection.apiUrl.replace(/\/$/, "");
  const baseUrl =
    import.meta.env.DEV && configuredUrl === DEFAULT_API_URL
      ? "/cloudflare-api"
      : configuredUrl;
  const url = `${baseUrl}${path}`;
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      typeof payload.error === "string"
        ? payload.error
        : `リクエストに失敗しました (${response.status})`,
    );
  }
  return payload as T;
}

export async function listTodos(connection: Connection) {
  const params = new URLSearchParams();
  if (connection.organizationId) {
    params.set("organization_id", connection.organizationId);
  }
  const query = params.size ? `?${params.toString()}` : "";
  return request<TodoListResponse>(
    connection,
    `/api/todos${query}`,
  );
}

export async function listOrganizations(connection: Connection) {
  return request<OrganizationListResponse>(connection, "/api/organizations");
}

export async function createOrganization(
  connection: Connection,
  name: string,
) {
  return request<Organization>(connection, "/api/organizations", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function createTodo(
  connection: Connection,
  draft: TodoDraft,
) {
  const created = await request<Todo>(connection, "/api/todos", {
    method: "POST",
    body: JSON.stringify({
      organization_id: connection.organizationId,
      ...compactDraft(draft),
    }),
  });
  if (draft.status === "todo") return created;
  return updateTodo(connection, created.id, draft);
}

export async function updateTodo(
  connection: Connection,
  todoId: string,
  draft: TodoDraft,
) {
  return request<Todo>(connection, `/api/todos/${encodeURIComponent(todoId)}`, {
    method: "PATCH",
    body: JSON.stringify({
      organization_id: connection.organizationId,
      ...draft,
    }),
  });
}

export async function deleteTodo(
  connection: Connection,
  todoId: string,
) {
  return request<{ deleted: boolean }>(
    connection,
    `/api/todos/${encodeURIComponent(todoId)}?organization_id=${encodeURIComponent(connection.organizationId)}`,
    { method: "DELETE" },
  );
}
